import { renderMultiSectionDigitalClockTimeView as renderClockTimeView } from '@mui/x-date-pickers/timeViewRenderers';

import {
  TIME_PICKER_COLUMN_ARROW_BUTTON_CLASS_NAME,
  TIME_PICKER_COLUMN_ARROW_CLASS_NAME,
  TIME_PICKER_COLUMN_ARROW_DOWN_CLASS_NAME,
  TIME_PICKER_COLUMN_ARROW_UP_CLASS_NAME,
  TIME_PICKER_MERIDIEM_COLUMN_CLASS_NAME,
  TIME_PICKER_SCROLL_STEP,
} from '../constants';

import type { FTimePickerChangeContext, FTimePickerClockRendererProps } from '../types';

export function createTimePickerViewRenderer(
  setSelectionView: (view: FTimePickerChangeContext['view']) => void,
) {
  function renderMultiSectionDigitalClockTimeView(props: FTimePickerClockRendererProps) {
    return renderClockTimeView({
      ...props,
      onChange: (value, selectionState, selectedView) => {
        setSelectionView(selectedView);
        try {
          props.onChange?.(value, selectionState, selectedView);
        } finally {
          setSelectionView(undefined);
        }
      },
    });
  }
  // MUI checks the renderer's name to include the AM/PM column, including in minified builds.
  Object.defineProperty(renderMultiSectionDigitalClockTimeView, 'name', { value: renderClockTimeView.name });
  return renderMultiSectionDigitalClockTimeView;
}

function buildColumnArrow(direction: 'up' | 'down', label: string, onClick: EventListener) {
  const arrowItem = document.createElement('div');
  arrowItem.className = [
    TIME_PICKER_COLUMN_ARROW_CLASS_NAME,
    direction === 'up'
      ? TIME_PICKER_COLUMN_ARROW_UP_CLASS_NAME
      : TIME_PICKER_COLUMN_ARROW_DOWN_CLASS_NAME,
  ].join(' ');
  arrowItem.setAttribute('role', 'presentation');

  const arrowButton = document.createElement('button');
  arrowButton.className = TIME_PICKER_COLUMN_ARROW_BUTTON_CLASS_NAME;
  arrowButton.type = 'button';
  arrowButton.setAttribute('aria-label', label);
  arrowButton.addEventListener('click', onClick);

  arrowItem.appendChild(arrowButton);

  return { arrowButton, arrowItem };
}

function centerSelectedSectionItem(section: HTMLElement) {
  const selectedItem = section.querySelector<HTMLElement>('[role="option"][aria-selected="true"]');
  if (selectedItem == null) {
    return;
  }

  section.scrollTop =
    selectedItem.offsetTop - (section.clientHeight - selectedItem.offsetHeight) / 2;
}

export function attachTimePickerColumnControls(paperElement: HTMLElement | null): () => void {
  if (paperElement == null) {
    return () => {};
  }

  const sections = Array.from(
    paperElement.querySelectorAll<HTMLElement>('.MuiMultiSectionDigitalClockSection-root'),
  );
  const cleanupTasks: Array<() => void> = [];

  sections.forEach((section, index) => {
    if (sections.length > 2 && index === sections.length - 1) {
      const resetScroll = () => {
        if (section.scrollTop !== 0) {
          section.scrollTop = 0;
        }
      };
      const preventScroll = (event: Event) => {
        event.preventDefault();
        resetScroll();
      };

      section.classList.add(TIME_PICKER_MERIDIEM_COLUMN_CLASS_NAME);
      resetScroll();
      window.requestAnimationFrame(resetScroll);
      section.addEventListener('scroll', resetScroll);
      section.addEventListener('wheel', preventScroll, { passive: false });
      section.addEventListener('touchmove', preventScroll, { passive: false });

      cleanupTasks.push(() => {
        section.classList.remove(TIME_PICKER_MERIDIEM_COLUMN_CLASS_NAME);
        section.removeEventListener('scroll', resetScroll);
        section.removeEventListener('wheel', preventScroll);
        section.removeEventListener('touchmove', preventScroll);
      });
      return;
    }

    const scrollUp = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      section.scrollBy({ top: -TIME_PICKER_SCROLL_STEP, behavior: 'smooth' });
    };
    const scrollDown = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      section.scrollBy({ top: TIME_PICKER_SCROLL_STEP, behavior: 'smooth' });
    };
    const clockRoot = section.parentElement;
    if (clockRoot == null) {
      return;
    }

    const columnLabel = index === 0 ? 'hours' : 'minutes';
    const upControl = buildColumnArrow('up', `Scroll ${columnLabel} up`, scrollUp);
    const downControl = buildColumnArrow('down', `Scroll ${columnLabel} down`, scrollDown);

    [upControl.arrowItem, downControl.arrowItem].forEach((arrowItem) => {
      arrowItem.style.left = `${section.offsetLeft}px`;
      arrowItem.style.width = `${section.offsetWidth}px`;
      clockRoot.appendChild(arrowItem);
    });
    const scheduleCenterSelectedItem = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => centerSelectedSectionItem(section));
      });
    };
    const selectedObserver = new MutationObserver(scheduleCenterSelectedItem);

    scheduleCenterSelectedItem();
    selectedObserver.observe(section, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected'],
    });

    cleanupTasks.push(() => {
      selectedObserver.disconnect();
      upControl.arrowButton.removeEventListener('click', scrollUp);
      downControl.arrowButton.removeEventListener('click', scrollDown);
      upControl.arrowItem.remove();
      downControl.arrowItem.remove();
    });
  });

  return () => {
    cleanupTasks.forEach((cleanupTask) => cleanupTask());
  };
}
